import * as go from 'gojs';

import { defaultGoJsLayeredDigraphLayoutSettings } from 'src/containers/ProjectDiagram/templates/extensions/layouts/constants';
import { ExtendedForceDirectedLayout } from 'src/containers/ProjectDiagram/templates/extensions/layouts/ForceDirectedLayout/ExtendedForceDirectedLayout';
import { LayeredDigraphLayoutSettings } from 'src/containers/ProjectDiagram/templates/extensions/layouts/types';

enum ParallelLinkQuadrant {
  BOTTOM_RIGHT = Math.PI / 4,
  BOTTOM_LEFT = (Math.PI * 3) / 4,
  TOP_RIGHT = -Math.PI / 4,
  TOP_LEFT = -(Math.PI * 3) / 4,
}

type LinkDirectionInfo = {
  pseudoAngle: number;
  quadrant: ParallelLinkQuadrant;
  isForward: boolean;
  isDownstream: boolean;
};

type ParallelLinkRibbon = {
  direction: LinkDirectionInfo;
  links: go.Link[];
};

export const computeLinkDirectionInfo = (
  link: go.Link,
  relativeToNode: go.Node,
  layoutIsHorizontal: boolean,
  loopBackLinks = false
): LinkDirectionInfo | null => {
  if (!link.fromNode?.position?.isReal() || !link.toNode?.position?.isReal()) {
    return null;
  }
  const otherNode =
    link.fromNode === relativeToNode ? link.toNode : link.fromNode;
  const dx = otherNode.position.x - relativeToNode.position.x;
  const dy = otherNode.position.y - relativeToNode.position.y;
  const isForward = layoutIsHorizontal
    ? link.toNode.position.x >= link.fromNode.position.x
    : link.toNode.position.y >= link.fromNode.position.y;
  const isDownstream = layoutIsHorizontal ? dx >= 0 : dy >= 0;
  let quadrant: ParallelLinkQuadrant;
  if (dx < 0 || (layoutIsHorizontal && loopBackLinks && !isForward)) {
    quadrant =
      dy < 0 || (!layoutIsHorizontal && loopBackLinks && !isForward)
        ? ParallelLinkQuadrant.TOP_LEFT
        : ParallelLinkQuadrant.BOTTOM_LEFT;
  } else {
    quadrant =
      dy < 0 || (!layoutIsHorizontal && loopBackLinks && !isForward)
        ? ParallelLinkQuadrant.TOP_RIGHT
        : ParallelLinkQuadrant.BOTTOM_RIGHT;
  }

  const denominator = Math.abs(dx) + Math.abs(dy);
  let pseudoAngle = 0;

  if (denominator !== 0) {
    pseudoAngle = layoutIsHorizontal ? -dy / denominator : dx / denominator;
  }

  // We only care about monotonic angle order for sorting ports, not precise
  // angle values (yet), so we don't actually need the expense of trigonometry
  // or square roots to sort links bottom to top or left to right:
  return {
    pseudoAngle,
    quadrant,
    isForward,
    isDownstream,
  };
};

export class ExtendedLayeredDigraphLayout extends go.LayeredDigraphLayout {
  layoutSettings: LayeredDigraphLayoutSettings;

  doFullLayout = false;

  backupForceDirectedLayout: ExtendedForceDirectedLayout;

  parallelPorts: Record<string, Record<string, go.Spot>> = {};

  pseudoAngles: Record<string, Record<string, LinkDirectionInfo>> = {};

  constructor({
    layoutSettings,
  }: {
    layoutSettings: LayeredDigraphLayoutSettings;
  }) {
    super({
      columnSpacing: layoutSettings.columnSpacing,
      direction: layoutSettings.direction,
      layerSpacing: layoutSettings.layerSpacing,
      setsPortSpots: layoutSettings.setsPortSpots,
    });

    this.layoutSettings = {
      ...defaultGoJsLayeredDigraphLayoutSettings,
      ...(layoutSettings || {}),
    };

    this.backupForceDirectedLayout = new ExtendedForceDirectedLayout({
      layoutSettings: layoutSettings.forceLayoutSettings,
    });
  }

  cloneProtected(copy: this) {
    super.cloneProtected(this);
    // eslint-disable-next-line no-param-reassign
    copy.layoutSettings = this.layoutSettings;
    // eslint-disable-next-line no-param-reassign
    copy.doFullLayout = this.doFullLayout;
    // eslint-disable-next-line no-param-reassign
    copy.backupForceDirectedLayout = this.backupForceDirectedLayout;
    // eslint-disable-next-line no-param-reassign
    copy.parallelPorts = this.parallelPorts;
    // eslint-disable-next-line no-param-reassign
    copy.pseudoAngles = this.pseudoAngles;
  }

  doLayout(coll: go.Diagram | go.Group | go.Iterable<go.Part>) {
    if (!this.diagram) {
      return;
    }

    if (this.doFullLayout) {
      this.clearParallelPorts();
      super.doLayout(coll);
      this.doFullLayout = false;

      // Run base class layout again to get stable positions.
      // Gojs has a lot of side effects from our custom link routing, and from
      // the .location setter called in redrawLayout, and go.LayeredDigraphLayout
      // seems to need real number positions in order to work correctly, so we
      // need to run the layout twice: once to set "good enough" positions with
      // wacky link routing, then another that fixes the link routing and sets
      // stable positions.
      super.doLayout(coll);
    } else {
      // TODO: super.doLayout resets this.network to null every time it finishes
      // + fully encapsulates all of its layer-positioning logic, so there's no
      // way to override or manipulate this.network in order to pin fixed nodes
      // properly, in terms of node layers, and positions within layers.
      // Defaulting to a force-directed layout for subsequent layouts this way
      // isn't ideal. To avoid this, we may need to switch to a different
      // library (i.e. other than go.js) that implements a Sugiyama layered
      // layout that supports pinning layer positions, or implement our own
      this.diagram.layout = this.backupForceDirectedLayout;
      this.backupForceDirectedLayout.doLayout(coll);
      this.diagram.layout = this;
    }
  }

  reset(doFullLayout: boolean) {
    this.doFullLayout = doFullLayout;
    this.clearParallelPorts();
  }

  clearParallelPorts() {
    this.parallelPorts = {};
  }

  get layoutIsHorizontal() {
    return this.layoutSettings.direction === 0;
  }

  updatePortOrdersForNode(node: go.Node) {
    if (node.key === undefined) {
      return;
    }
    this.parallelPorts[node.key] = {};

    const parallelLinks: Record<string, go.Link[]> = {};
    node.linksConnected.each((link) => {
      if (link.fromNode?.key && link.toNode?.key && link.key !== undefined) {
        const directionalKey = `${link.fromNode.key}-${link.toNode.key}`;
        parallelLinks[directionalKey] = [
          ...(parallelLinks[directionalKey] || []),
          link,
        ];
      }
    });

    const parallelRibbonsByQuadrant: Record<
      ParallelLinkQuadrant,
      ParallelLinkRibbon[]
    > = Object.values(ParallelLinkQuadrant).reduce(
      (agg, quadrant) => ({ ...agg, [quadrant]: [] }),
      {}
    );
    Object.values(parallelLinks).forEach((links) => {
      const direction = computeLinkDirectionInfo(
        // the way directionalKey is created ensures that all links in a ribbon
        // will have the same direction and pseudoAngle, so we only need one
        // sample
        links[0],
        node,
        this.layoutIsHorizontal
      );
      if (direction === null) {
        return;
      }

      if (links.length === 1) {
        // Any backwards links mean that this one still "counts" as being
        // parallel, and shouldn't be routed to orthogonal ports
        const oppositeDirectionalKey = `${(links[0].toNode as go.Node).key}-${
          (links[0].fromNode as go.Node).key
        }`;
        if (
          !parallelLinks[oppositeDirectionalKey] &&
          this.layoutSettings.nonParallelLinksAreOrthogonal
        ) {
          if (this.layoutIsHorizontal) {
            this.parallelPorts[node.key as string][links[0].key as string] =
              direction.quadrant === ParallelLinkQuadrant.TOP_LEFT ||
              direction.quadrant === ParallelLinkQuadrant.BOTTOM_LEFT
                ? go.Spot.Left
                : go.Spot.Right;
          } else {
            this.parallelPorts[node.key as string][links[0].key as string] =
              direction.quadrant === ParallelLinkQuadrant.TOP_LEFT ||
              direction.quadrant === ParallelLinkQuadrant.TOP_RIGHT
                ? go.Spot.Top
                : go.Spot.Bottom;
          }
          return;
        }
      }

      if (direction.isDownstream) {
        // The order of parallel links in node.linksConnected is consistent for
        // all nodes, which results in twisted ribbons with port orders being
        // exactly opposite each other. Reversing half (all downstream-facing)
        // links untwists this
        links.reverse();
      }

      parallelRibbonsByQuadrant[direction.quadrant] = [
        ...(parallelRibbonsByQuadrant[direction.quadrant] || []),
        { direction, links },
      ];
      // Store pseudoAngles as a quick way to tell when a node's ports need to
      // be recomputed
      links.forEach((link) => {
        this.pseudoAngles[node.key as string] = {
          ...(this.pseudoAngles[node.key as string] || {}),
          [link.key as string]: direction,
        };
      });
    });
    this.parallelPorts[node.key] = Object.entries(
      parallelRibbonsByQuadrant
    ).reduce(
      (agg, [quadrant, ribbons]) => ({
        ...agg,
        ...this.computePorts(parseFloat(quadrant), ribbons),
      }),
      this.parallelPorts[node.key]
    );
  }

  computePorts(quadrant: ParallelLinkQuadrant, ribbons: ParallelLinkRibbon[]) {
    if (ribbons.length === 0) {
      return {};
    }
    // Related issues from above w.r.t. the ribbons themselves twisting: always
    // sorting upstream/downstream ribbons with the same pseudoangle, as well as
    // always sorting ribbons with different pseudoangles the same way, will
    // always make the ribbons twist—so we need to reverse the order for half of
    // the ribbons (only need to check ribbonA.direction.isDownstream because we
    // call computePorts for quadrants separately; isDownstream will be the same
    // for ribbonA and ribbonB)
    ribbons.sort((ribbonA, ribbonB) => {
      const pseudoAngleDiff =
        ribbonA.direction.pseudoAngle - ribbonB.direction.pseudoAngle;
      if (pseudoAngleDiff === 0) {
        return ribbonA.direction.isDownstream ? 1 : -1;
      }
      return ribbonA.direction.isDownstream
        ? -pseudoAngleDiff
        : pseudoAngleDiff;
    });
    const nGapsBetweenRibbons = ribbons.length - 1;
    const nGapsBetweenParallelPorts = ribbons.reduce(
      (agg, ribbon) => agg + ribbon.links.length,
      0
    );
    const radiansBetweenParallelPorts =
      this.layoutSettings.maxParallelPortRadians /
      (this.layoutSettings.parallelPortRibbonSpacing * nGapsBetweenRibbons +
        nGapsBetweenParallelPorts);
    const radiansBetweenRibbons =
      radiansBetweenParallelPorts *
      this.layoutSettings.parallelPortRibbonSpacing;

    const ports: Record<string, go.Spot> = {};
    let angle = quadrant - this.layoutSettings.maxParallelPortRadians / 2;
    ribbons.forEach(({ links }) => {
      links.forEach((link) => {
        const x = Math.cos(angle);
        const y = Math.sin(angle);
        // go.js uses a (0,0 to 1,1) coordinate system for its ports; need to convert from (-1,-1 to 1,1)
        ports[link.key as string] = new go.Spot(x / 2 + 0.5, y / 2 + 0.5);

        angle += radiansBetweenParallelPorts;
      });
      angle += radiansBetweenRibbons;
    });
    return ports;
  }
}
